import { wcStoreFetchOrNull, wcStoreFetchOrThrow, wcStoreFetchResponse } from "./wcStoreFetch"
import {
  WCStoreCart,
  WCStoreCreateOrderRequestData,
  WCStoreCreateOrderResponseData,
  WCStoreOrderData,
  WCStoreProduct,
  WCUpdateUserAddressRequestData,
} from "./wcStoreTypes"

export async function wcGetCart() {
  return wcStoreFetchOrThrow<WCStoreCart>({ relativeUrl: "/cart" })
}

export async function wcAddItemToCartOrThrow(productId: number) {
  return wcStoreFetchOrThrow<WCStoreCart>(
    {
      method: "POST",
      relativeUrl: "/cart/add-item",
      body: JSON.stringify({
        id: productId,
        quantity: 1,
      }),
    },
    {
      // We want to ignore certain types of expected invalid coupon errors and
      // manually report unexpected ones.
      report4xxErrors: false,
    },
  )
}

export async function wcEditCartItemQuantity(key: string, quantity: number) {
  return wcStoreFetchOrThrow<WCStoreCart>({
    method: "POST",
    relativeUrl: `/cart/update-item?key=${key}&quantity=${quantity}`,
  })
}

export async function wcDeleteItemFromCart(key: string) {
  return wcStoreFetchOrThrow<WCStoreCart>({
    method: "POST",
    relativeUrl: `/cart/remove-item?key=${key}`,
  })
}

export async function wcApplyCouponOrThrow(code: string) {
  return wcStoreFetchOrThrow<WCStoreCart>(
    {
      method: "POST",
      relativeUrl: `/cart/apply-coupon/?code=${code.toLowerCase()}`,
    },
    {
      // We want to ignore certain types of expected invalid coupon errors and
      // manually report unexpected ones.
      report4xxErrors: false,
    },
  )
}

export async function wcRemoveCoupon(code: string) {
  return wcStoreFetchOrThrow<WCStoreCart>({
    method: "POST",
    relativeUrl: `/cart/remove-coupon/?code=${code}`,
  })
}

export async function wcUpdateCartAddressesOrThrow(requestData: WCUpdateUserAddressRequestData) {
  return wcStoreFetchOrThrow<WCStoreCart>(
    {
      method: "POST",
      relativeUrl: "/cart/update-customer",
      body: JSON.stringify(requestData),
    },
    {
      // We want to ignore certain types of expected invalid coupon errors and
      // manually report unexpected ones.
      report4xxErrors: false,
    },
  )
}

export async function wcCreateOrderOrThrow(data: WCStoreCreateOrderRequestData) {
  return wcStoreFetchOrThrow<WCStoreCreateOrderResponseData>(
    {
      method: "POST",
      relativeUrl: "/checkout",
      body: JSON.stringify(data),
    },
    {
      // We want to ignore certain types of expected invalid coupon errors and
      // manually report unexpected ones.
      report4xxErrors: false,
    },
  )
}

export async function wcGetOrder({ id, key, billingEmail }: { id: number; key: string; billingEmail: string }) {
  return wcStoreFetchOrThrow<WCStoreOrderData>({
    relativeUrl: `/order/${id}?key=${key}&billing_email=${billingEmail}`,
  })
}

export async function wcGetProductBySlugOrId(slugOrId: string | number) {
  return wcStoreFetchOrNull<WCStoreProduct>({
    relativeUrl: `/products/${slugOrId}`,
  })
}

export async function wcGetProductBySku(sku: string) {
  const products = await wcStoreFetchOrNull<WCStoreProduct[]>({
    relativeUrl: `/products?sku=${sku}`,
  })

  return products?.[0] || null
}

export async function wcGetVariationProductBySlugOrId(variationSlugOrId: string | number) {
  return wcStoreFetchOrNull<WCStoreProduct>({
    relativeUrl: `/products/${variationSlugOrId}?type=variation`,
  })
}

export async function wcGetVariationProductBySku(sku: string) {
  const products = await wcStoreFetchOrNull<WCStoreProduct[]>({
    relativeUrl: `/products?sku=${sku}&type=variation`,
  })

  return products?.[0] || null
}

export async function wcGetAllProducts({ variations, category }: { variations?: boolean; category?: number } = {}) {
  // These products are broken and cause a 500 error when fetched.
  const variationParam = variations ? "&type=variation" : ""
  const categoryParam = category ? `&category=${category}` : ""

  console.debug(`📃 Fetching all WooCommerce products... ${variationParam}${categoryParam}`)

  const path = "/products"
  const query = `?per_page=1${variationParam}${categoryParam}`
  const relativeUrl = `${path}${query}`
  const countResponse = await wcStoreFetchResponse({ relativeUrl })

  if (!countResponse.ok) {
    throw new Error(`Failed to fetch product count: ${countResponse.status}`)
  }

  const products: WCStoreProduct[] = []
  const count = +(countResponse.headers.get("X-Wp-Total") || 0)
  const perPage = 100
  const requiredPages = Math.ceil(count / perPage)
  console.debug(`📃 Found ${count} products. Fetching ${requiredPages} pages...`)

  for (let page = 1; page <= requiredPages; page++) {
    const query = `?per_page=${perPage}&page=${page}${variationParam}${categoryParam}`
    const relativeUrl = `${path}${query}`

    const chunk = await wcStoreFetchOrThrow<WCStoreProduct[]>({ relativeUrl })

    products.push(...(chunk || []))
  }

  return products
}
